import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NavigationService } from '../../../root/services/navigation.service';
import { CommsPreferencesDTO } from '../../model/communication-preferences.dto';
import { CommunicationPreferencesService } from '../../../root/services/communication-preferences.service';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from '../../services';
import { of } from 'rxjs';
import { communicationPreferencesFormBuilder } from '../../helpers/comms-preferences-form.builder';
import { TenantConfigService } from '../../../../../../../tenant-config/tenant-config.service';

@Component({
  selector: 'hfc-contact-preference',
  templateUrl: './contact-preference.component.html',
  styleUrls: ['./contact-preference.component.scss']
})
export class ContactPreferenceComponent implements OnInit {
  @Input() isStandalone = true;
  @Input() userEmail?: string;
  @Input() userPhoneNumber?: string;
  @Input() commsPreferences: CommsPreferencesDTO;
  @Input() tokenId?: string;
  @Input() clientId?: number;

  // Output for 4-step
  @Output() commsPreferencesChanged = new EventEmitter<CommsPreferencesDTO>();

  public isPaperless: boolean;
  public showPaperlessGraphic: boolean;
  public importantMarketingState: any = {};
  public optionalMarketingState: any = {};

  constructor(
    private readonly navigationService: NavigationService,
    private readonly commsPreferencesService: CommunicationPreferencesService,
    private readonly toastService: ToastService,
    private readonly tenantConfigService: TenantConfigService
  ) {}

  /**
   * Tenant-configured privacy-policy URL (e.g. the company's site).
   * Undefined for brands that haven't configured one — the template hides
   * the link rather than pointing at another brand's policy.
   */
  public get privacyPolicyUrl(): string | undefined {
    return this.tenantConfigService.privacyPolicyUrl;
  }

  ngOnInit(): void {
    // Inject clientId into commsPreferences in non-standalone (sign-up) flow
    if (!this.isStandalone && this.commsPreferences && this.clientId) {
      this.commsPreferences.clientId = this.clientId;
      this.commsPreferences.updatedBy = this.clientId;
      this.commsPreferences.automaticallySet = false;
    }
  }

  updateContactPreferencesStandalone() {
    const commsPreferenceData = communicationPreferencesFormBuilder(
      this.commsPreferences,
      this.importantMarketingState,
      this.optionalMarketingState
    );

    this.commsPreferencesService
      .setCommsPreferences(commsPreferenceData, this.tokenId)
      .pipe(
        tap(() => {
          // If API POST successful (204), re-direct user to generic results component
          this.navigateToSuccessScreen();
        }),
        catchError((error) => {
          if (error.status === 400) {
            this.toastService.add(
              { header: 'Please check your input and try again.', message: error.error.error.message },
              'error'
            );
          } else if (error.status === 404) {
            this.toastService.add({ header: "We couldn't find your details. Please try again." }, 'error');
          } else if (error.status === 500) {
            this.toastService.add({ header: 'Something went wrong on our side. Please try later.' }, 'error');
          } else {
            // Unknown error fallback
            this.toastService.add({ header: 'An unexpected error occurred.' }, 'error');
          }

          return of(null);
        })
      )
      .subscribe();
  }

  onOptionalUpdatesChanged(changes: any) {
    this.optionalMarketingState = {
      ...this.optionalMarketingState,
      ...changes
    };

    this.emitCommsPreferences();
    this.evaluatePaperlessStatus();
  }

  onImportantUpdatesChanged(changes: any) {
    this.importantMarketingState = {
      ...this.importantMarketingState,
      ...changes
    };

    this.emitCommsPreferences();
    this.evaluatePaperlessStatus();
  }

  onUpdatesChanged(showPaperlessGraphic: boolean) {
    this.showPaperlessGraphic = showPaperlessGraphic;
  }

  private evaluatePaperlessStatus() {
    const post = this.importantMarketingState?.essentialPost || this.optionalMarketingState?.nonEssentialPost;

    this.isPaperless = !post;
  }

  private navigateToSuccessScreen() {
    const extras = this.getResultExtras();
    this.navigationService.goToResultsComponent(
      extras.title,
      extras.message,
      extras.height,
      extras.width,
      extras.status,
      extras.url,
      true
    );
  }

  private getResultExtras() {
    return {
      title: 'Preferences Updated.',
      message: ' Please note you will continue to receive email and phone messaging for essential updates.',
      height: '3.75rem',
      width: '3.75rem',
      status: 'SUCCESS' as const,
      url: 'preferences-updated'
    };
  }

  private emitCommsPreferences(): void {
    const commsPreferenceData = communicationPreferencesFormBuilder(
      this.commsPreferences,
      this.importantMarketingState,
      this.optionalMarketingState
    );
    this.commsPreferencesChanged.emit(commsPreferenceData);
  }
}
